import { format, type Locale } from "date-fns";
import type { DatePreset, DateRange, DateRangePreset } from "./Preset";
import type { PickerProps } from "./DatePicker";

type CompareMode =
    | "previous_month"
    | "previous_period"
    | "same_period_last_year"
    | "same_period_last_month"
    | "custom";

type QuickSelectKey =
    | "yesterday"
    | "last_7_days"
    | "last_14_days"
    | "last_30_days"
    | "this_month"
    | "last_month"
    | "this_year"
    | "last_year";

const isBrowserLocaleClockType24h = () => {
    const language = typeof window !== "undefined" ? window.navigator.language : "en-US";

    const hr = new Intl.DateTimeFormat(language, {
        hour: "numeric",
    }).format();

    return Number.isInteger(Number(hr));
};

const formatDate = (date: Date, locale: Locale, includeTime?: boolean): string => {
    const usesAmPm = !isBrowserLocaleClockType24h();
    let dateString: string;

    if (includeTime) {
        dateString = usesAmPm ? format(date, "MMM do, yyyy h:mm a", { locale }) : format(date, "MMM do, yyyy HH:mm", { locale });
    } else {
        dateString = format(date, "MMM do, yyyy", { locale });
    }

    return dateString;
};

const validatePresets = (presets: DateRangePreset[] | DatePreset[], rules: PickerProps) => {
    const { toYear, fromYear, fromMonth, toMonth, fromDay, toDay } = rules;

    if (presets && presets.length > 0) {
        const fromYearToUse = fromYear;
        const toYearToUse = toYear;

        for (const preset of presets) {
            if ("date" in preset) {
                const presetYear = preset.date.getFullYear();

                if (fromYear && presetYear < fromYear) {
                    throw new Error(`Preset ${preset.label} is before fromYear ${fromYearToUse}.`);
                }

                if (toYear && presetYear > toYear) {
                    throw new Error(`Preset ${preset.label} is after toYear ${toYearToUse}.`);
                }

                if (fromMonth) {
                    const presetMonth = preset.date.getMonth();

                    if (presetMonth < fromMonth.getMonth()) {
                        throw new Error(`Preset ${preset.label} is before fromMonth ${fromMonth}.`);
                    }
                }

                if (toMonth) {
                    const presetMonth = preset.date.getMonth();

                    if (presetMonth > toMonth.getMonth()) {
                        throw new Error(`Preset ${preset.label} is after toMonth ${toMonth}.`);
                    }
                }

                if (fromDay) {
                    const presetDay = preset.date.getDate();

                    if (presetDay < fromDay.getDate()) {
                        throw new Error(`Preset ${preset.label} is before fromDay ${fromDay}.`);
                    }
                }

                if (toDay) {
                    const presetDay = preset.date.getDate();

                    if (presetDay > toDay.getDate()) {
                        throw new Error(`Preset ${preset.label} is after toDay ${format(toDay, "MMM dd, yyyy")}.`);
                    }
                }
            }

            if ("dateRange" in preset) {
                const presetFromYear = preset.dateRange.from?.getFullYear();
                const presetToYear = preset.dateRange.to?.getFullYear();

                if (presetFromYear && fromYear && presetFromYear < fromYear) {
                    throw new Error(`Preset ${preset.label}'s 'from' is before fromYear ${fromYearToUse}.`);
                }

                if (presetToYear && toYear && presetToYear > toYear) {
                    throw new Error(`Preset ${preset.label}'s 'to' is after toYear ${toYearToUse}.`);
                }

                if (fromMonth) {
                    const presetMonth = preset.dateRange.from?.getMonth();

                    if (presetMonth && presetMonth < fromMonth.getMonth()) {
                        throw new Error(`Preset ${preset.label}'s 'from' is before fromMonth ${format(fromMonth, "MMM, yyyy")}.`);
                    }
                }

                if (toMonth) {
                    const presetMonth = preset.dateRange.to?.getMonth();

                    if (presetMonth && presetMonth > toMonth.getMonth()) {
                        throw new Error(`Preset ${preset.label}'s 'to' is after toMonth ${format(toMonth, "MMM, yyyy")}.`);
                    }
                }

                if (fromDay) {
                    const presetDay = preset.dateRange.from?.getDate();

                    if (presetDay && presetDay < fromDay.getDate()) {
                        throw new Error(`Preset ${preset.dateRange.from}'s 'from' is before fromDay ${format(fromDay, "MMM dd, yyyy")}.`);
                    }
                }

                if (toDay) {
                    const presetDay = preset.dateRange.to?.getDate();

                    if (presetDay && presetDay > toDay.getDate()) {
                        throw new Error(`Preset ${preset.label}'s 'to' is after toDay ${format(toDay, "MMM dd, yyyy")}.`);
                    }
                }
            }
        }
    }
};

const startOfDay = (date: Date) => {
    const d = new Date(date.getTime());
    d.setHours(0, 0, 0, 0);
    return d;
};

const addDays = (date: Date, days: number) => {
    const d = new Date(date.getTime());
    d.setDate(d.getDate() + days);
    return d;
};

const clampDayInMonth = (year: number, monthIndex: number, day: number) => {
    const lastDay = new Date(year, monthIndex + 1, 0).getDate();
    return Math.min(day, lastDay);
};

const addMonthsClamped = (date: Date, months: number) => {
    const year = date.getFullYear();
    const month = date.getMonth() + months;
    const targetYear = year + Math.floor(month / 12);
    const targetMonth = ((month % 12) + 12) % 12;
    const day = clampDayInMonth(targetYear, targetMonth, date.getDate());
    const d = new Date(date.getTime());
    d.setFullYear(targetYear, targetMonth, day);
    return d;
};

const addYearsClamped = (date: Date, years: number) => {
    const targetYear = date.getFullYear() + years;
    const targetMonth = date.getMonth();
    const day = clampDayInMonth(targetYear, targetMonth, date.getDate());
    const d = new Date(date.getTime());
    d.setFullYear(targetYear, targetMonth, day);
    return d;
};

const diffInCalendarDays = (later: Date, earlier: Date) => {
    const ms = startOfDay(later).getTime() - startOfDay(earlier).getTime();
    return Math.round(ms / (24 * 60 * 60 * 1000));
};

const getCompareRange = (mode: Exclude<CompareMode, "custom">, baseRange: DateRange): DateRange | undefined => {
    if (!baseRange.from || !baseRange.to) {
        return undefined;
    }

    const from = startOfDay(baseRange.from);
    const to = startOfDay(baseRange.to);

    switch (mode) {
        case "previous_month": {
            const prevMonthDate = addMonthsClamped(from, -1);
            const prevFrom = startOfDay(new Date(prevMonthDate.getFullYear(), prevMonthDate.getMonth(), 1));
            const prevTo = startOfDay(new Date(prevMonthDate.getFullYear(), prevMonthDate.getMonth() + 1, 0));
            return { from: prevFrom, to: prevTo };
        }
        case "previous_period": {
            const lengthDays = diffInCalendarDays(to, from) + 1;
            const prevTo = addDays(from, -1);
            const prevFrom = addDays(prevTo, -(lengthDays - 1));
            return { from: prevFrom, to: prevTo };
        }
        case "same_period_last_month": {
            return { from: startOfDay(addMonthsClamped(from, -1)), to: startOfDay(addMonthsClamped(to, -1)) };
        }
        case "same_period_last_year": {
            return { from: startOfDay(addYearsClamped(from, -1)), to: startOfDay(addYearsClamped(to, -1)) };
        }
    }
};

const getQuickSelectRange = (key: QuickSelectKey, now: Date): DateRange => {
    const today = startOfDay(now);

    switch (key) {
        case "yesterday": {
            const y = startOfDay(addDays(today, -1));
            return { from: y, to: y };
        }
        case "last_7_days": {
            return { from: startOfDay(addDays(today, -6)), to: today };
        }
        case "last_14_days": {
            return { from: startOfDay(addDays(today, -13)), to: today };
        }
        case "last_30_days": {
            return { from: startOfDay(addDays(today, -29)), to: today };
        }
        case "this_month": {
            const from = startOfDay(new Date(today.getFullYear(), today.getMonth(), 1));
            return { from, to: today };
        }
        case "last_month": {
            const from = startOfDay(new Date(today.getFullYear(), today.getMonth() - 1, 1));
            const to = startOfDay(new Date(today.getFullYear(), today.getMonth(), 0));
            return { from, to };
        }
        case "this_year": {
            const from = startOfDay(new Date(today.getFullYear(), 0, 1));
            return { from, to: today };
        }
        case "last_year": {
            const from = startOfDay(new Date(today.getFullYear() - 1, 0, 1));
            const to = startOfDay(new Date(today.getFullYear() - 1, 11, 31));
            return { from, to };
        }
    }
};

const getQuickSelectKeyFromRange = (range: DateRange, now: Date): QuickSelectKey | undefined => {
    if (!range.from || !range.to) {
        return undefined;
    }

    const from = startOfDay(range.from);
    const to = startOfDay(range.to);

    const keys: QuickSelectKey[] = ["yesterday", "last_7_days", "last_14_days", "last_30_days", "this_month", "last_month", "this_year", "last_year"];

    for (const key of keys) {
        const expected = getQuickSelectRange(key, now);
        if (!expected.from || !expected.to) {
            continue;
        }

        if (expected.from.getTime() === from.getTime() && expected.to.getTime() === to.getTime()) {
            return key;
        }
    }

    return undefined;
};

export { isBrowserLocaleClockType24h, formatDate, validatePresets, getCompareRange, getQuickSelectRange, getQuickSelectKeyFromRange, type CompareMode, type QuickSelectKey };
